"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import DeferredKundaliFormCard from "../_home/DeferredKundaliFormCard";
import TopicSelect, { clearConcernSelection, type ConcernSelection } from "./TopicSelect";
import type { ChatSessionPrice } from "./chat-session-price";
import {
  AI_ASTROLOGER_ONBOARDING_CONTRACT,
  AI_ASTROLOGER_TRUST_COPY,
  chatPricingCopy,
} from "./onboarding-contract";
import styles from "./ConsultationHero.module.css";

export default function AIAstrologerConsultationIsland({
  price = null,
}: {
  price?: ChatSessionPrice | null;
}) {
  const pricing = chatPricingCopy(price);
  const [choice, setChoice] = useState<
    { kind: "selected"; entry: ConcernSelection } | { kind: "skipped" } | null
  >(null);
  const [announcement, setAnnouncement] = useState("");
  const [focusRequest, setFocusRequest] = useState(0);
  const conversionRef = useRef<HTMLDivElement>(null);
  const moveToForm = useCallback((message: string) => {
    setAnnouncement(message);
    setFocusRequest((request) => request + 1);
  }, []);
  const handleIntentCaptured = useCallback(
    (entry: ConcernSelection) => {
      setChoice({ kind: "selected", entry });
      moveToForm(AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.selectedAnnouncement);
    },
    [moveToForm]
  );
  const handleSkip = useCallback(() => {
    setChoice({ kind: "skipped" });
    moveToForm(AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.skippedAnnouncement);
  }, [moveToForm]);

  useEffect(() => {
    if (focusRequest === 0) return;
    conversionRef.current
      ?.querySelector<HTMLElement>("[data-field='name'], #name")
      ?.focus({ preventScroll: true });
  }, [focusRequest]);

  const selectedConcern = choice?.kind === "selected" ? choice.entry : null;
  const seoEntryOverride =
    choice?.kind === "selected" ? choice.entry : choice?.kind === "skipped" ? null : undefined;

  // The concern picker is OPTIONAL refinement, NOT a gate: the chat-continuation
  // form (which generates the chart and opens the AI Jyotish chat) leads the first
  // useful viewport, matching the proven /free-kundali/ converter (rule 61 #2). The
  // old TopicSelect gate hid the form behind a concern tap, so only ~0.37% of
  // /ai-astrologer/ landers (3,226/day) ever reached an offer. When a concern IS
  // picked it still seeds the greeting via seoEntryOverride; when it isn't,
  // KundaliFormCard resolves the generic entry (useResolvedSEOEntry), so no concern
  // is required to proceed. Phone is optional on the shared card (owner decision
  // 2026-09-24); no offer is added to this chat-first surface (rules 39/40/46).
  //
  // The form renders as ONE screen. `progressiveCopy` (a 3-step gated wizard) shipped
  // here in 3270f2e10 and first SERVED 2026-07-16T18:52Z (2026-07-17 00:22 IST). From
  // that instant, measured on the owned beacon with /free-kundali/ as a natural control:
  //   submitted/started on /ai-astrologer/  0.821 -> 0.624   (-24%, sustained 13 days)
  //   submitted/started on /free-kundali/   0.744 -> 0.748   (flat; no progressive form)
  // The wizard's own step telemetry shows why: three gated screens compound three
  // abandonment points (identity 75.5% x chart 93.5% x contact 71.9% = 50.8%) where the
  // single screen converted at 82%. The stated intent — "phone is the FINAL required
  // save/reopen step" — needs no wizard: it is already carried by the field order and by
  // `firstErrorField` (KundaliFormCard.validation.ts), which puts phone/email last. So
  // the intent is kept and the gate is dropped. Pinned by the `data-progressive` false
  // assertion below and by the repo-wide no-production-callsite pin in
  // KundaliFormCard.progressive.callsites.test.tsx.
  return (
    <div ref={conversionRef} translate="no" data-agent-bridge="inline">
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
      {selectedConcern ? (
        <div className={styles.selectedConcern}>
          <p>
            {selectedConcern.topicTitle}
            <span>{selectedConcern.topicLens}</span>
          </p>
          <button
            type="button"
            aria-label={AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.changeLabel}
            onClick={() => {
              clearConcernSelection();
              setChoice(null);
              setAnnouncement("Concern choice reset.");
            }}
            className={styles.changeButton}
          >
            Change
          </button>
        </div>
      ) : choice?.kind === "skipped" ? (
        <div className={styles.selectedConcern}>
          <p>
            {AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.skipTitle}
            <span>{AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.skipLens}</span>
          </p>
          <button
            type="button"
            aria-label={AI_ASTROLOGER_ONBOARDING_CONTRACT.concerns.changeLabel}
            onClick={() => {
              setChoice(null);
              setAnnouncement("Concern choice reset.");
            }}
            className={styles.changeButton}
          >
            Change
          </button>
        </div>
      ) : (
        <div className="mx-auto max-w-md">
          <TopicSelect onIntentCaptured={handleIntentCaptured} onSkip={handleSkip} />
        </div>
      )}
      <DeferredKundaliFormCard
        source="ai_astrologer"
        submitLabel="Generate chart and start chat"
        loadingLabel="Opening your AI Jyotish chat..."
        loadingLabelHi="AI Jyotish chat khul raha hai..."
        reassuranceNote={pricing.en.formReassurance}
        reassuranceNoteHi={pricing.hi.formReassurance}
        phoneReason={AI_ASTROLOGER_TRUST_COPY.en.savedNote}
        phoneReasonHi={AI_ASTROLOGER_TRUST_COPY.hi.savedNote}
        showDailyInsightCard={false}
        containerClassName="mx-auto max-w-md"
        formClassName="space-y-5"
        fallbackTitle={AI_ASTROLOGER_TRUST_COPY.en.formTitle}
        fallbackBody={AI_ASTROLOGER_TRUST_COPY.en.formBody}
        fallbackEyebrow={null}
        placePlaceholder="City or birth place"
        seoEntryOverride={seoEntryOverride}
        preferRememberedJourney
        showLanguageToggle={false}
        showIntentBanner={false}
        interactionOnly
      />
    </div>
  );
}
